import { useEffect, useRef, useState } from "react";
import {
  BoltOutlined,
  BrushOutlined,
  CalendarMonthOutlined,
  CloseOutlined,
  CreditCardOutlined,
  EmojiEventsOutlined,
  GroupsOutlined,
  HistoryOutlined,
  PersonOutlineOutlined,
  PublicOutlined,
  RestartAltOutlined,
  ScheduleOutlined,
  SearchOutlined,
  SendOutlined,
  SettingsOutlined,
  EditOutlined,
  AddOutlined,
} from "@mui/icons-material";
import {
  getAdminUsers,
  getNotifications,
  sendNotification,
} from "../../../../services/Dashboard/Table";
import { formatDateTime } from "../../../../Utils/formatDateTime";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

const NOTIFICATION_TYPES = [
  { id: "race_alert", label: "Race Alert", Icon: EmojiEventsOutlined, color: "var(--color-primary)", bgColor: "rgba(200,255,0,0.12)" },
  { id: "subscription", label: "Subscription", Icon: CreditCardOutlined, color: "#00A3FF", bgColor: "rgba(0,163,255,0.12)" },
  { id: "system", label: "System", Icon: SettingsOutlined, color: "var(--color-accent-amber)", bgColor: "rgba(255,168,0,0.12)" },
  { id: "custom", label: "Custom", Icon: AddOutlined, color: "#9B59B6", bgColor: "rgba(155,89,182,0.12)" },
];

const NOTIFICATION_AUDIENCES = [
  { id: "all", label: "All Users", Icon: PublicOutlined, accent: "var(--color-accent-amber)", bgColor: "rgba(255,168,0,0.12)" },
  { id: "group", label: "Group", Icon: GroupsOutlined, accent: "#00A3FF", bgColor: "rgba(0,163,255,0.12)" },
  { id: "individual", label: "Individual", Icon: PersonOutlineOutlined, accent: "var(--color-primary)", bgColor: "rgba(200,255,0,0.12)" },
];

function NotificationOptionButton({ active, accent, Icon, label, bgColor, onClick }) {
  const activeColor = accent || "var(--color-primary)";
  const defaultBgColor = bgColor || "rgba(200,255,0,0.12)";

  const activeBorderClass =
    activeColor === "#00A3FF"
      ? "border-[rgba(0,163,255,0.25)]"
      : activeColor === "#9B59B6"
        ? "border-[rgba(155,89,182,0.25)]"
        : activeColor === "var(--color-accent-amber)"
          ? "border-[rgba(255,168,0,0.25)]"
          : "border-[rgba(200,255,0,0.25)]";

  const activeTextClass =
    activeColor === "#00A3FF"
      ? "text-[#00A3FF]"
      : activeColor === "#9B59B6"
        ? "text-[#9B59B6]"
        : activeColor === "var(--color-accent-amber)"
          ? "text-[var(--color-accent-amber)]"
          : "text-[var(--color-primary)]";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-4 py-3 font-extrabold md:text-sm text-xs border rounded-[var(--radius-md)] cursor-pointer ${active
        ? `bg-[${defaultBgColor}] ${activeBorderClass} ${activeTextClass}`
        : "bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-muted)]"
        }`}
    >
      <Icon sx={{ fontSize: 18 }} />
      {label}
    </button>
  );
}

function NotificationsModule() {
  // ── Tab ──
  const [mode, setMode] = useState("compose");

  // ── Compose ──
  const [type, setType] = useState("race_alert");
  const [audience, setAudience] = useState("all");
  const [sendTime, setSendTime] = useState("now");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [scheduleDate, setScheduleDate] = useState(() => dayjs().tz("Australia/Melbourne").format("YYYY-MM-DD"));
  const [scheduleTime, setScheduleTime] = useState(() => dayjs().tz("Australia/Melbourne").format("HH:mm"));
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ── User search (Group / Individual) ──
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchWrapRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // ── History ──
  const [histFilter, setHistFilter] = useState("All");
  const [notifications, setNotifications] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histLoadingMore, setHistLoadingMore] = useState(false);
  const [histError, setHistError] = useState("");
  const [histPage, setHistPage] = useState(1);
  const [histHasMore, setHistHasMore] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [histSearch, setHistSearch] = useState("");
  const [selectedHistItem, setSelectedHistItem] = useState(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (selectedHistItem) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedHistItem]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && selectedHistItem) {
        setSelectedHistItem(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedHistItem]);

  // Debounced user search
  useEffect(() => {
    if (!userQuery.trim()) {
      setUserResults([]);
      setShowDropdown(false);
      return;
    }
    const t = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const data = await getAdminUsers(userQuery);
        const arr = Array.isArray(data) ? data : (data.results || []);
        setUserResults(arr.filter(user => user.is_active));
        setShowDropdown(true);
      } catch {
        setUserResults([]);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [userQuery]);

  // Auto-hide success banner after 10 seconds
  useEffect(() => {
    if (sent) {
      const timer = setTimeout(() => {
        setSent(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [sent]);

  // Auto-hide submit error after 10 seconds
  useEffect(() => {
    if (submitError) {
      const timer = setTimeout(() => {
        setSubmitError("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [submitError]);

  // Debounce history search text
  useEffect(() => {
    const t = setTimeout(() => {
      setHistSearch(searchText);
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // Load history on tab switch / search / filter changes (always reset to page 1)
  useEffect(() => {
    if (mode === "history") {
      loadHistory(histSearch, filterToFilterType(histFilter), 1, false);
    }
  }, [mode, histSearch, histFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll: auto-load more when sentinel becomes visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && histHasMore && !histLoadingMore && !histLoading) {
          loadHistory(histSearch, filterToFilterType(histFilter), histPage + 1, true);
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [histHasMore, histLoadingMore, histLoading, histPage, histSearch, histFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Maps UI filter label → backend filter_type value
  const filterToFilterType = (f) => {
    return f; // "All", "Broadcast", "Group", "Individual", "System" → passed directly
  };

  const loadHistory = async (
    search = histSearch,
    filterType = filterToFilterType(histFilter),
    pageNum = 1,
    append = false
  ) => {
    if (pageNum === 1) setHistLoading(true);
    else setHistLoadingMore(true);
    setHistError("");
    try {
      const data = await getNotifications(pageNum, 15, search, filterType);
      const arr = Array.isArray(data) ? data : (data.results || []);
      const count = Array.isArray(data) ? data.length : (data.count || 0);
      setNotifications((prev) => {
        const newItems = append ? [...prev, ...arr.map(mapHistItem)] : arr.map(mapHistItem);
        setHistHasMore(newItems.length < count);
        return newItems;
      });
      setHistPage(pageNum);
    } catch {
      setHistError("Failed to load notifications.");
    } finally {
      setHistLoading(false);
      setHistLoadingMore(false);
    }
  };

  const getTypeConfig = (t) => {
    switch (t) {
      case "prediction":
        return { Icon: EmojiEventsOutlined, color: "var(--color-primary)", bgColor: "rgba(200,255,0,0.12)", border: "rgba(200,255,0,0.25)" };
      case "subscription":
        return { Icon: CreditCardOutlined, color: "#00A3FF", bgColor: "rgba(0,163,255,0.12)", border: "rgba(0,163,255,0.25)" };
      case "system":
        return { Icon: SettingsOutlined, color: "var(--color-accent-amber)", bgColor: "rgba(255,168,0,0.12)", border: "rgba(255,168,0,0.25)" };
      case "promo":
      default:
        return { Icon: BrushOutlined, color: "#9B59B6", bgColor: "rgba(155,89,182,0.12)", border: "rgba(155,89,182,0.25)" };
    }
  };

  const mapHistItem = (item) => {
    let status = "Pending";
    if (item.status === "sent") status = "Delivered";
    else if (item.status === "failed") status = "Failed";
    let aud = "All Users";
    if (item.target_type === "user") aud = "Individual";
    else if (item.target_type === "group") aud = "Group";
    const fmt = (d) =>
      d ? formatDateTime(d) : null;
    return {
      id: item.id,
      title: item.title || "(no title)",
      body: item.body || "",
      type: item.type,
      audience: aud,
      target:
        item.target_type === "user"
          ? (item.target_user_details?.name || item.target_user_email || `User #${item.target_user}`)
          : aud,
      status,
      when: fmt(item.created_at),
      targetUserDetails: item.target_user_details,
      targetUsersCount: item.target_users_count || 0,
      targetUsersPreview: item.target_users_preview || [],
      scheduledAt: fmt(item.scheduled_at),
      sentAt: fmt(item.sent_at),
      createdAt: fmt(item.created_at),
      senderName: item.sender_details?.name || item.sender_details?.email || "System",
    };
  };

  const handleAudienceChange = (id) => {
    setAudience(id);
    setSelectedUsers([]);
    setUserQuery("");
    setShowDropdown(false);
  };

  const addUser = (user) => {
    if (audience === "individual") setSelectedUsers([user]);
    else if (!selectedUsers.find((u) => u.id === user.id))
      setSelectedUsers((p) => [...p, user]);
    setUserQuery("");
    setShowDropdown(false);
  };

  const removeUser = (id) => setSelectedUsers((p) => p.filter((u) => u.id !== id));

  const getUserName = (u) =>
    u.first_name && u.last_name
      ? `${u.first_name} ${u.last_name}`
      : u.first_name || u.username || u.email || "User";

  const canSubmit =
    title.trim().length > 0 &&
    message.trim().length > 0 &&
    (sendTime === "now" || (scheduleDate && scheduleTime)) &&
    (audience === "all" ||
      (audience === "individual" && selectedUsers.length === 1) ||
      (audience === "group" && selectedUsers.length > 0));

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const typeMap = { race_alert: "prediction", subscription: "subscription", system: "system", custom: "promo" };
      const payload = {
        title,
        body: message,
        type: typeMap[type] || "promo",
        target_type: audience === "all" ? "all" : audience === "group" ? "group" : "user",
      };
      if (audience === "individual" && selectedUsers.length > 0)
        payload.target_user = selectedUsers[0].id;
      if (audience === "group" && selectedUsers.length > 0)
        payload.data = { target_user_ids: selectedUsers.map((u) => u.id) };
      if (sendTime === "schedule" && scheduleDate && scheduleTime) {
        // Interpret the selected date/time as Australian timezone (not the browser's local timezone),
        // then convert to UTC ISO string for the backend.
        const ausDateTime = dayjs.tz(`${scheduleDate} ${scheduleTime}`, "Australia/Melbourne");
        payload.scheduled_at = ausDateTime.utc().format();
      }

      await sendNotification(payload);
      setSent(true);
      setTitle(""); setMessage(""); setSelectedUsers([]);
      setUserQuery(""); setScheduleDate(""); setScheduleTime("");

    } catch (err) {
      setSubmitError(err?.error || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = (t) =>
    ({ prediction: "Race Alert", subscription: "Subscription", system: "System", promo: "Custom" }[t] || "Custom");

  const currentType = NOTIFICATION_TYPES.find((t) => t.id === type);

  // Shared user-search dropdown UI
  const renderUserSearch = (singleMode) => (
    <>
      {singleMode && selectedUsers.length === 1 ? (
        <div className="notif-selected-user-card">
          <span className="notif-user-avatar">
            {(selectedUsers[0].first_name || selectedUsers[0].username || "?")[0].toUpperCase()}
          </span>
          <span className="flex-1 min-w-0">
            <strong className="block text-[var(--color-text)] md:text-base text-sm">
              {getUserName(selectedUsers[0])}
            </strong>
            <em className="text-[var(--color-text-muted)] md:text-sm text-xs not-italic">
              {selectedUsers[0].email}
            </em>
          </span>
          <button type="button" className="notif-x-btn" onClick={() => setSelectedUsers([])} aria-label="Remove">×</button>
        </div>
      ) : (
        <div className="notif-search-wrap" ref={searchWrapRef}>
          <div className="notif-search-row">
            <SearchOutlined sx={{ fontSize: 18, flexShrink: 0, color: "var(--color-text-muted)" }} />
            <input
              className="notif-search-input"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder={singleMode ? "Search by name or email…" : "Search users to add…"}
              autoComplete="off"
            />
            {isSearchingUsers && <span className="notif-spin" />}
            {userQuery && !isSearchingUsers && (
              <button type="button" className="notif-x-btn"
                onClick={() => { setUserQuery(""); setShowDropdown(false); }}>×</button>
            )}
          </div>
          {showDropdown && (
            <div className="notif-dropdown">
              {userResults.length === 0 ? (
                <div className="notif-dropdown-empty">
                  {singleMode
                    ? <PersonOutlineOutlined sx={{ fontSize: 28, color: "var(--color-text-muted)" }} />
                    : <GroupsOutlined sx={{ fontSize: 28, color: "var(--color-text-muted)" }} />}
                  <p>No users found</p>
                </div>
              ) : (
                userResults.map((u) => {
                  const already = selectedUsers.some((s) => s.id === u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={`notif-dropdown-item${already ? " already-added" : ""}`}
                      onClick={() => !already && addUser(u)}
                      disabled={already}
                    >
                      <span className="notif-user-avatar">
                        {(u.first_name || u.username || "?")[0].toUpperCase()}
                      </span>
                      <span className="flex-1 min-w-0 text-left">
                        <span className="block font-bold text-[var(--color-text)] xl:text-base md:text-sm text-xs">
                          {getUserName(u)}
                        </span>
                        <span className="block text-[var(--color-text-muted)] xl:text-sm md:text-xs text-[10px]">
                          {u.email}
                        </span>
                      </span>
                      {already && <span className="text-[var(--color-primary)] xl:text-lg md:text-base text-sm">✓</span>}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
      {!singleMode && selectedUsers.length > 0 && (
        <div className="notif-chips-row">
          <span className="notif-chip-count">
            {selectedUsers.length} user{selectedUsers.length > 1 ? "s" : ""} selected
          </span>
          {selectedUsers.map((u) => (
            <span key={u.id} className="notif-chip">
              {getUserName(u)}
              <button type="button" onClick={() => removeUser(u.id)}>×</button>
            </span>
          ))}
        </div>
      )}
    </>
  );

  const typeBgColorClass =
    currentType?.id === "subscription"
      ? "bg-[rgba(0,163,255,0.12)]"
      : currentType?.id === "system"
        ? "bg-[rgba(255,168,0,0.12)]"
        : currentType?.id === "custom"
          ? "bg-[rgba(155,89,182,0.12)]"
          : "bg-[rgba(200,255,0,0.12)]";

  const isAllUsers = selectedHistItem?.audience === "All Users";
  const isGroupUsers = selectedHistItem?.audience === "Group";
  const modalAudBg = isAllUsers ? "bg-[rgba(255,168,0,0.12)]" : isGroupUsers ? "bg-[rgba(0,163,255,0.12)]" : "bg-[rgba(200,255,0,0.12)]";
  const modalAudText = isAllUsers ? "!text-[var(--color-accent-amber)]" : isGroupUsers ? "!text-[#00A3FF]" : "!text-[var(--color-primary)]";
  const modalAudBorder = isAllUsers ? "border-[rgba(255,168,0,0.25)]" : isGroupUsers ? "border-[rgba(0,163,255,0.25)]" : "border-[rgba(200,255,0,0.25)]";

  const modalTypeBg =
    selectedHistItem?.type === "prediction"
      ? "bg-[rgba(200,255,0,0.12)]"
      : selectedHistItem?.type === "subscription"
        ? "bg-[rgba(0,163,255,0.12)]"
        : selectedHistItem?.type === "system"
          ? "bg-[rgba(255,168,0,0.12)]"
          : "bg-[rgba(155,89,182,0.12)]";

  const modalTypeText =
    selectedHistItem?.type === "prediction"
      ? "!text-[var(--color-primary)]"
      : selectedHistItem?.type === "subscription"
        ? "!text-[#00A3FF]"
        : selectedHistItem?.type === "system"
          ? "!text-[var(--color-accent-amber)]"
          : "!text-[#9B59B6]";

  const modalTypeBorder =
    selectedHistItem?.type === "prediction"
      ? "border-[rgba(200,255,0,0.25)]"
      : selectedHistItem?.type === "subscription"
        ? "border-[rgba(0,163,255,0.25)]"
        : selectedHistItem?.type === "system"
          ? "border-[rgba(255,168,0,0.25)]"
          : "border-[rgba(155,89,182,0.25)]";

  const statusTextClass =
    selectedHistItem?.status === "Delivered"
      ? "!text-[var(--color-primary)]"
      : selectedHistItem?.status === "Failed"
        ? "!text-[var(--color-accent-red)]"
        : "!text-[var(--color-accent-amber)]";

  const statusBgClass =
    selectedHistItem?.status === "Delivered"
      ? "bg-[var(--color-primary)]"
      : selectedHistItem?.status === "Failed"
        ? "bg-[--color-accent-red]"
        : "bg-[var(--color-accent-amber)]";

  return (
    <div className="admin-notifications-module">
      {/* ── Tab bar ── */}
      <div className="admin-notification-mode-tabs">
        <button type="button" onClick={() => setMode("compose")} className={mode === "compose" ? "active left" : "left"}>
          <EditOutlined sx={{ fontSize: 19 }} /> Compose
        </button>
        <button type="button" onClick={() => setMode("history")} className={mode === "history" ? "active right" : "right"}>
          <HistoryOutlined sx={{ fontSize: 20 }} /> History
        </button>
      </div>

      {/* ── COMPOSE TAB ── */}
      {mode === "compose" && (
        <div className="admin-notification-card">
          {sent && (
            <div className="notif-success-banner">
              ✓ Notification queued successfully!
            </div>
          )}
          {submitError && (
            <div className="notif-error-banner">{submitError}</div>
          )}

          {/* Notification Type */}
          <div className="notification-field-group">
            <p className="label-caps">Notification Type</p>
            <div className="notification-option-grid four">
              {NOTIFICATION_TYPES.map((item) => (
                <NotificationOptionButton
                  key={item.id}
                  active={type === item.id}
                  accent={item.color}
                  Icon={item.Icon}
                  label={item.label}
                  bgColor={item.bgColor}
                  onClick={() => setType(item.id)}
                />
              ))}
            </div>
          </div>

          {/* Target Audience */}
          <div className="notification-field-group">
            <p className="label-caps">Target Audience</p>
            <div className="notification-option-grid three">
              {NOTIFICATION_AUDIENCES.map((item) => (
                <NotificationOptionButton
                  key={item.id}
                  active={audience === item.id}
                  accent={item.accent}
                  Icon={item.Icon}
                  label={item.label}
                  bgColor={item.bgColor}
                  onClick={() => handleAudienceChange(item.id)}
                />
              ))}
            </div>
          </div>

          {/* Individual – single user only */}
          {audience === "individual" && (
            <div className="notification-field-group">
              <p className="label-caps">Select User</p>
              {renderUserSearch(true)}
            </div>
          )}

          {/* Group – multi-select */}
          {audience === "group" && (
            <div className="notification-field-group">
              <p className="label-caps">Search &amp; Select Users</p>
              {renderUserSearch(false)}
            </div>
          )}

          {/* Title */}
          <label className="notification-field-group">
            <span className="label-caps">Notification Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 80))}
              placeholder="Enter a short, clear title…"
              className="notification-input"
            />
            <span className="notification-count">{title.length}/80</span>
          </label>

          {/* Body */}
          <label className="notification-field-group">
            <span className="label-caps">Message Body</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 200))}
              placeholder="Write the notification message…"
              className="notification-input notification-textarea"
            />
            <span className="notification-count">{message.length}/200</span>
          </label>

          <div className="notification-divider" />

          {/* Send time */}
          <div className="notification-field-group">
            <p className="label-caps">Send Time</p>
            <div className="notification-option-grid two">
              <NotificationOptionButton active={sendTime === "now"} Icon={BoltOutlined} label="Now" onClick={() => setSendTime("now")} />
              <NotificationOptionButton active={sendTime === "schedule"} Icon={CalendarMonthOutlined} label="Schedule" onClick={() => setSendTime("schedule")} />
            </div>
          </div>

          {sendTime === "schedule" && (
            <div className="notification-field-group">
              <p className="label-caps">Schedule (AEST)</p>
              <div className="notification-option-grid two">
                <label className="notification-date-input"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector("input");
                    if (input && typeof input.showPicker === "function") {
                      try {
                        input.showPicker();
                        if (e.target.tagName !== "INPUT") {
                          e.preventDefault();
                        }
                      } catch (err) {
                        console.warn("Failed to show date picker:", err);
                      }
                    }
                  }}>
                  <CalendarMonthOutlined sx={{ fontSize: 20 }} />
                  <input type="date" value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" || e.key === "Delete") {
                        setScheduleDate("");
                      } else if (e.key !== "Tab" && e.key !== "Escape" && e.key !== "Enter") {
                        e.preventDefault();
                      }
                    }}
                    min={new Date().toISOString().split("T")[0]}
                    aria-label="Notification date" />
                </label>
                <label className="notification-date-input"
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector("input");
                    if (input && typeof input.showPicker === "function") {
                      try {
                        input.showPicker();
                        if (e.target.tagName !== "INPUT") {
                          e.preventDefault();
                        }
                      } catch (err) {
                        console.warn("Failed to show time picker:", err);
                      }
                    }
                  }}>
                  <ScheduleOutlined sx={{ fontSize: 20 }} />
                  <input type="time" value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" || e.key === "Delete") {
                        setScheduleTime("");
                      } else if (e.key !== "Tab" && e.key !== "Escape" && e.key !== "Enter") {
                        e.preventDefault();
                      }
                    }}
                    aria-label="Notification time" />
                </label>
              </div>
            </div>
          )}

          {/* Live preview */}
          {(title.length > 0 || message.length > 0) && (
            <div className="notification-field-group notif-preview-box">
              <p className="label-caps">Push Preview</p>
              <div className="notif-preview-card">
                <div className={`notif-preview-icon ${typeBgColorClass}`}>
                  {currentType && <currentType.Icon sx={{ fontSize: 18, color: currentType.color || "var(--color-primary)" }} />}
                </div>
                <div className="flex-1 gap-1 min-w-0">
                  <p className="notif-preview-title">{title || "Notification Title"}</p>
                  <p className="notif-preview-body">{message || "Message body appears here."}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <button type="button" disabled={!canSubmit || submitting} className="notification-submit" onClick={handleSubmit}>
            <SendOutlined sx={{ fontSize: 22 }} />
            {submitting ? "Sending…" : sendTime === "now" ? "Send Notification" : "Schedule Notification"}
          </button>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {mode === "history" && (
        <div className="admin-notification-card">
          {/* Filter pills + refresh */}
          <div className="notif-hist-header">
            <div className="notif-filter-pills">
              {["All", "Broadcast", "Group", "Individual", "System"].map((f) => (
                <button key={f} type="button"
                  className={`notif-filter-pill${histFilter === f ? " active" : ""}`}
                  onClick={() => setHistFilter(f)}>{f}</button>
              ))}
            </div>

            <button type="button" className="notif-refresh-btn" onClick={() => loadHistory(histSearch, filterToFilterType(histFilter), 1, false)} title="Refresh">
              <RestartAltOutlined sx={{ fontSize: 18 }} />
            </button>
          </div>

          {/* Search bar */}
          <div className="notif-search-container">
            <SearchOutlined sx={{ fontSize: 18, flexShrink: 0, color: "var(--color-text-muted)" }} />
            <input
              type="text"
              placeholder="Search notifications by title or message..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="notif-search-input"
            />
            {searchText && (
              <button
                type="button"
                className="notif-x-btn"
                onClick={() => { setSearchText(""); loadHistory("", filterToFilterType(histFilter), 1, false); }}
              >
                &times;
              </button>
            )}
          </div>

          <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
            {histLoading ? (
              <div className="grid gap-px">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]"
                  />
                ))}
              </div>
            ) : histError ? (
              <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-accent-red)] text-center">{histError}</div>
            ) : notifications.length === 0 ? (
              <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-text-muted)] text-center">No notifications found</div>
            ) : (
              <>
                {notifications.map((item) => {
                  const typeConfig = getTypeConfig(item.type);

                  // Tailwind class maps
                  const typeBgClass =
                    item.type === "prediction"
                      ? "bg-[rgba(200,255,0,0.12)]"
                      : item.type === "subscription"
                        ? "bg-[rgba(0,163,255,0.12)]"
                        : item.type === "system"
                          ? "bg-[rgba(255,168,0,0.12)]"
                          : "bg-[rgba(155,89,182,0.12)]";

                  const typeTextClass =
                    item.type === "prediction"
                      ? "text-[var(--color-primary)]"
                      : item.type === "subscription"
                        ? "text-[#00A3FF]"
                        : item.type === "system"
                          ? "text-[var(--color-accent-amber)]"
                          : "text-[#9B59B6]";

                  const typeBorderClass =
                    item.type === "prediction"
                      ? "border-[rgba(200,255,0,0.25)]"
                      : item.type === "subscription"
                        ? "border-[rgba(0,163,255,0.25)]"
                        : item.type === "system"
                          ? "border-[rgba(255,168,0,0.25)]"
                          : "border-[rgba(155,89,182,0.25)]";

                  const isAllAudience = item.audience.toLowerCase().includes("all");
                  const isGroupAudience = item.audience.toLowerCase() === "group";
                  const audBgClass = isAllAudience ? "bg-[rgba(255,168,0,0.12)]" : isGroupAudience ? "bg-[rgba(0,163,255,0.12)]" : "bg-[rgba(200,255,0,0.12)]";
                  const audTextClass = isAllAudience ? "text-[var(--color-accent-amber)]" : isGroupAudience ? "text-[#00A3FF]" : "text-[var(--color-primary)]";
                  const audBorderClass = isAllAudience ? "border-[rgba(255,168,0,0.25)]" : isGroupAudience ? "border-[rgba(0,163,255,0.25)]" : "border-[rgba(200,255,0,0.25)]";

                  const getAudienceLabel = (aud) => {
                    if (aud === "Individual") return "INDIVIDUAL";
                    if (aud === "All Users") return "BROADCAST";
                    return "GROUP";
                  };

                  const statusColors =
                    item.status === "Pending"
                      ? "bg-[rgba(255,168,0,0.12)] text-[var(--color-accent-amber)] border-[rgba(255,168,0,0.25)]"
                      : item.status === "Failed"
                        ? "bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)] border-[rgba(255,69,0,0.25)]"
                        : "bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)] border-[rgba(200,255,0,0.25)]"

                  return (
                    <article
                      key={item.id}
                      className="notification-history-row"
                      onClick={() => setSelectedHistItem(item)}
                    >
                      <div className="notif-icon-name-section">
                        <div
                          className={`notif-hist-icon-box border ${typeBgClass} ${typeTextClass} ${typeBorderClass}`}
                        >
                          <typeConfig.Icon sx={{ fontSize: 20 }} />
                        </div>
                        <span
                          className={`notif-badge border ${audBgClass} ${audTextClass} ${audBorderClass}`}
                        >
                          {getAudienceLabel(item.audience)}
                        </span>
                      </div>

                      <div className="notif-row-content">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-bold leading-none">{item.title}</h4>
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full md:text-xs text-[10px] font-black uppercase tracking-[0.05em] border ${statusColors}`}
                          >
                            <span className="md:w-2 md:h-2 w-1.5 h-1.5 rounded-full bg-current" />
                            {item.status}
                          </div>
                        </div>
                        <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5]">{item.body}</p>
                      </div>

                      <div className="notif-row-meta">
                        {item.senderName && (
                          <div className="notif-sender">
                            By {item.senderName}
                          </div>
                        )}

                        <div className="notif-hist-time">
                          <ScheduleOutlined sx={{ fontSize: { xs: 14, sm: 16 }}} />
                          <span>{item.when || "—"}</span>
                        </div>
                      </div>
                    </article>
                  );
                })}

                {/* Lazy loading sentinel */}
                {histHasMore && (
                  <div ref={sentinelRef} className="p-4 flex items-center justify-center gap-2 border-t border-[var(--color-border)]">
                    {histLoadingMore ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                        <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Loading more...</span>
                      </>
                    ) : (
                      <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Scroll for more</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── DETAILED DIALOG/MODAL ── */}
          {selectedHistItem && (
            <div className="notif-modal-overlay" onClick={() => setSelectedHistItem(null)}>
              <div className="notif-modal-card" onClick={(e) => e.stopPropagation()}>
                <header className="notif-modal-header">
                  <h3>
                    Notification <span className="text-[var(--color-primary)]">Details</span>
                  </h3>
                  <button type="button" className="notif-modal-close" onClick={() => setSelectedHistItem(null)}>
                    <CloseOutlined sx={{ fontSize: 20 }} />
                  </button>
                </header>
                <div className="notif-modal-body no-scrollbar">
                  <div className="notif-modal-grid">
                    <div className="notif-modal-field">
                      <span className="notif-modal-label">Title</span>
                      <span className="notif-modal-val">{selectedHistItem.title}</span>
                    </div>
                    <div className="notif-modal-field">
                      <span className="notif-modal-label">Message</span>
                      <span className="notif-modal-val message">{selectedHistItem.body}</span>
                    </div>
                    <div className="notif-modal-row-grid">
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Audience</span>
                        <span className={`notif-modal-val badge inline-block w-fit py-1.5 px-3 rounded-full uppercase border ${modalAudBg} ${modalAudText} ${modalAudBorder}`}>{selectedHistItem.audience}</span>
                      </div>
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Category</span>
                        <span className={`notif-modal-val badge inline-block w-fit py-1.5 px-3 rounded-full uppercase border ${modalTypeBg} ${modalTypeText} ${modalTypeBorder}`}>{typeLabel(selectedHistItem.type)}</span>
                      </div>
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Status</span>
                        <span className={`notif-modal-val status-val ${statusTextClass}`}>
                          <span className={`notif-status-dot ${statusBgClass}`} />
                          {selectedHistItem.status}
                        </span>
                      </div>
                    </div>
                    <div className="notif-modal-row-grid">
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Sent By</span>
                        <span className="notif-modal-val">{selectedHistItem.senderName || "System"}</span>
                      </div>
                    </div>
                    <div className="notif-modal-row-grid">
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Created At</span>
                        <span className="notif-modal-val">{selectedHistItem.createdAt || "—"}</span>
                      </div>
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Scheduled At</span>
                        <span className="notif-modal-val">{selectedHistItem.scheduledAt || "—"}</span>
                      </div>
                      <div className="notif-modal-field">
                        <span className="notif-modal-label">Sent At</span>
                        <span className="notif-modal-val">{selectedHistItem.sentAt || "—"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationsModule;
